import type { ProctorSignalRMessage } from "@/utils/useProctorSignalR";
import { sortRealtimeResponsesForDisplay } from "@/utils/testDetailsResponses";

export type ProctorLiveRealtimeRow = {
  id?: number;
  sectionId?: number;
  formId?: number;
  question?: string;
  response?: string;
  time?: string;
  clientTime?: string;
};

export type ProctorLiveResponseRow = {
  sectionId?: number;
  questionId?: string | number;
  field?: string | number;
  value?: string | number;
  response?: string | number;
  when?: string;
};

export type ProctorLiveSection = {
  sectionId?: number;
  submitTime?: string;
};

export type ProctorLiveSnapshot = {
  id?: number;
  code?: string;
  realtimeResponses?: ProctorLiveRealtimeRow[];
  responses?: ProctorLiveResponseRow[];
  sections?: ProctorLiveSection[];
};

function normalizeValue(value: unknown) {
  if (value === null || value === undefined) return "";
  return String(value);
}

export function isProctorMessageForSnapshot(
  snapshot: ProctorLiveSnapshot | null | undefined,
  message: ProctorSignalRMessage | null | undefined,
): boolean {
  if (!snapshot || !message) return false;
  const currentTestId = Number(snapshot.id || 0);
  const incomingTestId = Number(message.testId || 0);
  const sameTestById = currentTestId > 0 && incomingTestId > 0 && currentTestId === incomingTestId;
  const sameTestByCode = !!snapshot.code && !!message.code && snapshot.code === message.code;
  return sameTestById || sameTestByCode;
}

function isDuplicateRealtime(
  row: ProctorLiveRealtimeRow,
  message: ProctorSignalRMessage,
): boolean {
  return Number(row.sectionId || 0) === Number(message.sectionId || 0)
    && Number(row.formId || 0) === Number(message.formId || 0)
    && normalizeValue(row.question) === normalizeValue(message.questionId)
    && normalizeValue(row.response) === normalizeValue(message.response)
    && normalizeValue(row.time) === normalizeValue(message.time);
}

export function applyProctorLiveUpdate<T extends ProctorLiveSnapshot>(
  snapshot: T | null | undefined,
  message: ProctorSignalRMessage | null | undefined,
): T | null | undefined {
  if (!snapshot || !message || !isProctorMessageForSnapshot(snapshot, message)) {
    return snapshot;
  }

  if (message.eventType === "realtime") {
    const existing = snapshot.realtimeResponses || [];
    if (existing.some((row) => isDuplicateRealtime(row, message))) {
      return snapshot;
    }

    return {
      ...snapshot,
      realtimeResponses: sortRealtimeResponsesForDisplay([
        ...existing,
        {
          ...(message.id != null ? { id: message.id } : {}),
          sectionId: message.sectionId,
          formId: message.formId,
          question: message.questionId,
          response: message.response,
          time: message.time,
          ...(message.clientTime ? { clientTime: message.clientTime } : {}),
        },
      ]),
    };
  }

  if (message.eventType === "sectionSubmitted") {
    const values = Array.isArray(message.values) ? message.values : [];
    const sectionAlreadySubmitted = (snapshot.sections || []).some((section) =>
      Number(section.sectionId || 0) === Number(message.sectionId || 0)
      && normalizeValue(section.submitTime) === normalizeValue(message.submittedAt),
    );
    if (sectionAlreadySubmitted) {
      return snapshot;
    }

    const submittedAt = message.submittedAt;
    const nextResponses = [
      ...(snapshot.responses || []),
      ...values.map((item) => ({
        sectionId: message.sectionId,
        questionId: item.questionId,
        field: item.questionId,
        value: item.response,
        response: item.response,
        when: submittedAt,
      })),
    ];
    const nextSections = (snapshot.sections || []).map((section) =>
      Number(section.sectionId || 0) === Number(message.sectionId || 0)
        ? { ...section, submitTime: submittedAt }
        : section,
    );

    return {
      ...snapshot,
      responses: nextResponses,
      sections: nextSections,
    };
  }

  return snapshot;
}
